import { Server } from './server';
import { Service, ServiceResponse } from './service';

export class UserService extends Service {
  protected user: any;
  private friendPresence: Map<string, any>;

  constructor() {
    super();
    this.user = null;
    this.friendPresence = new Map();
    this.onFriendConnect = this.onFriendConnect.bind(this);
    this.onFriendDisconnect = this.onFriendDisconnect.bind(this);
    this.onFriendPresence = this.onFriendPresence.bind(this);
    this.onFriendInvited = this.onFriendInvited.bind(this);
    this.onFriendAccepted = this.onFriendAccepted.bind(this);
    this.onFriendDeclined = this.onFriendDeclined.bind(this);
    this.onFriendRemoved = this.onFriendRemoved.bind(this);
  }

  public async init(): Promise<ServiceResponse> {
    Server.network.addEventListener('friend-connect', this.onFriendConnect);
    Server.network.addEventListener('friend-disconnect', this.onFriendDisconnect);
    Server.network.addEventListener('friend-presence', this.onFriendPresence);
    Server.network.addEventListener('friend-invited', this.onFriendInvited);
    Server.network.addEventListener('friend-accepted', this.onFriendAccepted);
    Server.network.addEventListener('friend-declined', this.onFriendDeclined);
    Server.network.addEventListener('friend-removed', this.onFriendRemoved);
    return {success: true};
  }
  
  public async sync(): Promise<ServiceResponse> {
    let response = await this.sendCommand('user', 'get-user');

    if(response.status == 404) {
      response = await this.sendCommand('user', 'create-user');
      
      if(response.status != 200)
        return {success: false, message: response.body.message};

      response = await this.sendCommand('user', 'get-user');
    }
    
    if(response.status != 200)
      return {success: false};

    this.user = response.body.user;

    if(!this.user.chats)
      this.user.chats = [];

    let now = new Date();
    this.user.presence = {online: true, location: 'site', date: now.toISOString()};

    // add expected properties
    if(!this.user.friends) this.user.friends = [];
    if(!this.user.followers) this.user.followers = [];
    if(!this.user.following) this.user.following = [];
    if(!this.user.communities) this.user.communities = [];
    if(!this.user.cookies) this.user.cookies = [];

    // cache profiles
    Server.public.addProfileToCache(this.user);

    // friend presence
    for(let i = 0; i < this.user.friends.length; i++) 
      this.setFriendPresence(this.user.friends[i].id, {online: false});

    if(this.user.friends.length > 0) {
      response = await this.sendCommand('user', 'get-friends-online');

      if(response.status == 200) {
        for(let i = 0; i < response.body.friends.length; i++) {
          let onlineFriend = response.body.friends[i];
          this.setFriendPresence(onlineFriend.id, {
            date: onlineFriend.date,
            location: onlineFriend.location,
            online: true
          });
        }
      }

      setTimeout(() => {
        if(!this.user) return;
        for(let i = 0; i < this.user.friends.length; i++) {
          let state = this.user.friends[i].state; 
          if(state == 'invited' || state == 'declined')
            this.notifyListeners('friend-updated', {friend: this.user.friends[i].id, action: state})
        }
      }, 3000);
    }

    return {success: true};
  }

  public clear() {
    this.user = null;
    this.friendPresence.clear();
  }

  public async reset() {
    await this.sendCommand('user', 'reset-user');
    await this.sync();
    return {success: true};
  }

  // profile
  public getId(): string {
    if(!this.user)
      return '';
    return this.user.id;
  }

  public isCurrent(id:string):boolean {
    return this.user.id == id;
  }

  public getName(): string {
    if(!this.user)
      return '';
    return this.user.name;
  }

  public getSlug(): string {
    if(!this.user)
      return '';
    return this.user.slug;
  }

  public getBanner(): string {
    if(!this.user || !this.user.banner)
      return '';
    return this.user.banner;
  }

  public getPortrait(): string {
    if(!this.user || !this.user.portrait)
      return '';
    return this.user.portrait;
  }

  public getBio(): string {
    if(!this.user || !this.user.bio)
      return '';
    return this.user.bio;
  }

  public getRole(): string {
    if(!this.user || !this.user.role)
      return '';
    return this.user.role;
  }

  public getFollowersCount(): number {
    if(!this.user) return 0;
    return this.user.followers;
  }

  public getFollowingCount(): number {
    if(!this.user) return 0;
    return this.user.following;
  }

  public getProfile():any {
    if(!this.user)
      return null;
      
    return {
      id: this.user.id,
      name: this.user.name,
      slug: this.user.slug,
      banner: this.user.banner,
      portrait: this.user.portrait,
      bio: this.user.bio
    }
  }

  public async setName(name:string) {
    let response = await this.sendCommand('user', 'set-name', {name: name});
    
    if(response.status != 200)
      return {success: false, message: response.body.message};

    this.user.name = name;

    this.notifyListeners('user-profile-updated');

    return {success: true};
  }

  public setCachedName(name:string) {
    this.user.name = name;
  }

  public setCachedPortrait(portrait:string) {
    this.user.portrait = portrait;
  }

  public async setSlug(slug:string) {
    let response = await this.sendCommand('user', 'set-slug', {slug});
    
    if(response.status != 200)
      return {success: false, message: response.body.message};

    this.user.slug = slug;

    return {success: true};
  }

  public async setPortrait(portrait:string) {
    let response = await this.sendCommand('user', 'set-portrait', {portrait});
    
    if(response.status != 200)
      return {success: false, message: response.body.message};

    this.user.portrait = portrait;

    this.notifyListeners('user-profile-updated');

    return {success: true};
  }

  public async setBanner(banner:string) {
    let response = await this.sendCommand('user', 'set-banner', {banner});
    
    if(response.status != 200)
      return {success: false, message: response.body.message};

    this.user.banner = banner;

    return {success: true};
  }

  public async setBio(bio:string) {
    let response = await this.sendCommand('user', 'set-bio', {bio});
    
    if(response.status != 200)
      return {success: false, message: response.body.message};

    this.user.bio = bio;

    return {success: true};
  }

  public async setProfile(params: {slug?:string, name?:string, bio?:string, banner?:string, portrait?:string, user?:string}) {
    let response = await this.sendCommand('user', 'set-profile', params);
    
    if(response.status != 200)
      return {success: false, message: response.body.message};

    if(params.user) {
      return {success: false, message: 'Admin feature unsupported.'};
    }
    else {
      if(params.name)
        this.user.name = params.name;
      if(params.slug)
        this.user.slug = params.slug;
      if(params.bio)
        this.user.bio = params.bio;
      if(params.portrait)
        this.user.portrait = params.portrait;
      if(params.banner)
        this.user.banner = params.banner;
    }

    this.notifyListeners('user-profile-updated');

    return {success: true};
  }

  // chats
  public getChats():any[] {
    if(!this.user || !this.user.chats)
      return [];
    return this.user.chats;
  }

  public getChat(id:string):any {
    if(!this.user || !this.user.chats)
      return null;
    
    for(let i = 0; i < this.user.chats.length; i++)
      if(this.user.chats[i].id == id)
        return this.user.chats[i];
    
    return null;
  }

  public setChats(chats:any[]) {
    this.user.chats = chats;
  }

  public startChat(chat:any) {
    if(!this.user) return;
    if(this.user.chats.find((c:any) => c.id == chat.id)) return;
    this.user.chats.push(chat);
  }

  public updateChat(chat:any) {
    if(!this.user) return;
    let i = this.user.chats.findIndex((c:any) => c.id == chat.id);
    if(i != -1) return
    this.user.chats[i] = chat;
  }

  public leaveChat(id:string) {
    if(!this.user) return;
    let i = this.user.chats.findIndex((c:any) => c.id == id);
    if(i != -1)
      this.user.chats.splice(i, 1);
  }

  // cookies
  public findCookie(key:string) {
    for(let i = 0; i < this.user.cookies.length; i++)
      if(this.user.cookies[i].key == key)
        return this.user.cookies[i];
    return null;
  }

  public async setCookie(params:{user?:string, key:string, value:string}) {
    if(!this.user)
      return {success: false};

    let response = await this.sendCommand('user', 'set-cookie', params);
    if(response.status != 200)
      return {success: false, message: response.body.message};

    if(!params.user) {
      let cookie = this.findCookie(params.key);
      if(cookie)
        cookie.value = params.value;
      else
        this.user.cookies.push({key: params.key, value: params.value});
    }

    return {success: true};
  }

  public async deleteCookie(params:{user?:string, key:string}) {
    let response = await this.sendCommand('user', 'delete-cookie', params);
    if(response.status != 200)
      return {success: false, message: response.body.message};

    if(!params.user) {
      let cookie = this.findCookie(params.key);
      if(cookie) {
        let i = this.user.cookies.indexOf(cookie);
        this.user.cookies.splice(i, 1);
      }
    }

    return {success: true};
  }

  public getCookies(): any[] {
    return this.user.cookies;
  }

  public getCookie(key:string): string {
    if(!this.user) return null;
    for(let i = 0; i < this.user.cookies.length; i++)
      if(this.user.cookies[i].key == key)
        return this.user.cookies[i].value;
    return null;
  }

  public async loadCookies(user:string) {
    let response = await this.sendCommand('user', 'get-cookies', {user});
    if(response.status != 200)
      return {success: false, message: response.body.message};

    return {success: true, cookies: response.body.cookies};
  }

  // follows
  public async follow(id:string) {
    let response = await this.sendCommand('user', 'follow', {id});
    if(response.status != 200)
      return {success: false, message: response.body.message};
    this.user.following.push(id);
    return {success: true};
  }

  public async unfollow(id:string) {
    let response = await this.sendCommand('user', 'unfollow', {id});
    if(response.status != 200)
      return {success: false, message: response.body.message};

    let i = this.user.following.indexOf(id);
    if(i != -1)
      this.user.following.splice(i, 1);

    return {success: true};
  }

  public isFollowing(id:string) {
    return this.user.following.indexOf(id) != -1;
  }

  protected getFollows(users:any[]) {
    let follows = [];

    for(let i = 0; i < this.user.friends.length; i++)
      follows.push(this.user.friends[i].id);

    for(let i = 0; i < users.length; i++)
      if(follows.indexOf(users[i]) == -1)
        follows.push(users[i]);
        
    return follows;
  }

  public getFollowing() {
    return this.getFollows(this.user.following);
  }

  public getFollowers() {
    return this.getFollows(this.user.followers);
  }

  // friends
  public async inviteFriend(id:string) {
    let response = await this.sendCommand('user', 'invite-friend', {id});
    if(response.status != 200)
      return {success: false, message: response.body.message};

    let now = new Date();

    this.user.friends.push({
      id: id, 
      date: now.toISOString(), 
      state: 'pending'
    });

    return {success: true};
  }

  public async inviteFriendBySlug(slug:string) {
    let response = await this.sendCommand('user', 'invite-friend', {slug});
    if(response.status != 200)
      return {success: false, message: response.body.message};

    let now = new Date();
    let friendId = response.body.id;

    this.user.friends.push({
      id: friendId, 
      date: now.toISOString(), 
      state: 'pending'
    });

    return {success: true};
  }

  public async removeFriend(id:string) {
    let response = await this.sendCommand('user', 'remove-friend', {id});
    if(response.status != 200)
      return {success: false, message: response.body.message};

    let friend = this.getFriend(id);

    if(friend) {
      let i = this.user.friends.indexOf(friend);
      if(i != -1)
        this.user.friends.splice(i, 1);
    }
  
    return {success: true};
  }

  public async acceptFriend(id:string) {
    let response = await this.sendCommand('user', 'accept-friend', {id});
    if(response.status != 200)
      return {success: false, message: response.body.message};

    let friend = this.getFriend(id);
    if(friend)
      friend.state = 'accepted';

    return {success: true};
  }

  public async declineFriend(id:string) {
    let response = await this.sendCommand('user', 'decline-friend', {id});
    if(response.status != 200)
      return {success: false, message: response.body.message};

    let friend = this.getFriend(id);
    if(friend) {
      let i = this.user.friends.indexOf(friend);
      if(i != -1)
        this.user.friends.splice(i, 1);
    }
    
    return {success: true};
  }

  public getFriendsFromCache() {
    if(!this.user)
      return [];
    return this.user.friends;
  }

  public isFriend(id:string):boolean {
    for(let i = 0; i < this.user.friends.length; i++) 
      if(this.user.friends[i].id == id)
        return (this.user.friends[i].state == 'accepted');
    
    return false;
  }

  public getFriend(id:string):any {
    for(let i = 0; i < this.user.friends.length; i++) 
      if(this.user.friends[i].id == id)
        return this.user.friends[i];
    return null;
  }

  public async getFriends() {
    let response = await this.sendCommand('user', 'get-friends');
    if(response.status != 200)
      return {success: false, message: response.body.message};
    this.user.friends = response.body.friends;
    return {success: true};
  }

  public async getFriendFollows(id:string) {
    let response = await this.sendCommand('user', 'get-friend-follows', {id});
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true, following: response.body.following, followers: response.body.followers};
  }

  // logging
  public async getLog() {
    let response = await this.sendCommand('user', 'get-log');
    if(response.status != 200)
      return {success: false, message: response.body.message};

    return {success: true, log: response.body.log};
  }

  // network event handlers
  protected onFriendConnect(event:any) {
    let friend = this.getFriend(event.friend);
    if(!friend) return;

    this.setFriendPresence(event.friend, {
      date: event.date,
      location: 'site',
      online: true
    });

    this.notifyFriendUpdated(event);
  }

  protected onFriendDisconnect(event:any) {
    let friend = this.getFriend(event.friend);
    if(!friend) return;

    this.setFriendPresence(event.friend, {online: false});
    
    this.notifyFriendUpdated(event);
  }

  protected onFriendPresence(event:any) {
    let friend = this.getFriend(event.friend);
    if(!friend) return;

    this.setFriendPresence(event.friend, {
      date: event.date,
      location: event.location,
      online: true
    });

    this.notifyFriendUpdated(event);
  }

  protected async onFriendInvited(event:any) {
    let friend = this.getFriend(event.friend);

    if(!friend) {
      friend = {
        id: event.friend,
        state: 'invited'
      };
      this.user.friends.push(friend);
    }
    else
      friend.state = 'invited';

    this.setFriendPresence(event.friend, {
      online: true,
      location: 'site',
      date: event.date
    });

    await Server.public.loadProfiles([event.friend]);

    this.notifyFriendUpdated(event);
  }

  protected onFriendAccepted(event:any) {
    let friend = this.getFriend(event.friend);
    if(!friend)
      return;

    friend.state = 'accepted';

    this.setFriendPresence(event.friend, {
      online: true,
      location: 'site',
      date: event.date
    });

    this.notifyFriendUpdated(event);
  }

  protected onFriendDeclined(event:any) {
    for(let i = 0; i < this.user.friends.length; i++) {
      if(this.user.friends[i].id == event.friend) {
        this.user.friends.splice(i, 1);
        this.notifyFriendUpdated(event);
        return;
      }
    }
  }

  protected onFriendRemoved(event:any) {
    for(let i = 0; i < this.user.friends.length; i++) {
      if(this.user.friends[i].id == event.friend) {
        this.user.friends.splice(i, 1);
        this.notifyFriendUpdated(event);
        return;
      }
    }
  }

  protected notifyFriendUpdated(event:any) {
    this.notifyListeners('friend-updated', {friend: event.friend, action: event.id.substring(7)});
  }

  // community
  public async createCommunity(params:any) {
    let response = await this.sendCommand('user', 'create-community', params);
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true, id: response.body.id};
  }

  public async updateCommunity(params:any) {
    let response = await this.sendCommand('user', 'update-community', params);
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true};
  }

  public getCommunities() {
    return this.user.communities;
  }

  public addCommunityToCache(community:any) {
    this.user.communities.push(community);
  }

  public async sendCommunityInvite(params:any) {
    let response = await this.sendCommand('user', 'send-community-invite', params);
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true};
  }

  public async getCommunityInvites() {
    let response = await this.sendCommand('user', 'get-community-invites');
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true, invites: response.body.invites};
  }

  public async getLatestNotificationDate() {
    let response = await this.sendCommand('user', 'get-latest-notification-date');
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true, date: response.body.date};
  }

  // friend presence management
  public getFriendPresence(id: string): any {
    return this.friendPresence.get(id) || {online: false};
  }

  public setFriendPresence(id: string, presence: any): void {
    this.friendPresence.set(id, presence);
  }

  public clearFriendPresence(id: string): void {
    this.friendPresence.delete(id);
  }

  public clearAllFriendPresence(): void {
    this.friendPresence.clear();
  }
}
